import { CollectionDetails } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/CollectionDetails";
import { NextPage } from "next";
import Head from "next/head";
// @ts-ignore unused
import React from "react";

import { siteUrl, ssrMode } from "@app/constants";
import { ProductListPage } from "@components/pages/ProductList/ProductListPage";
import { Attribute } from "@graphql/gqlTypes/Attribute";
import { useAttributesFiltering } from "@hooks/useAttributesFiltering";
import useProductFiltering from "@hooks/useProductFiltering";
import { useProductLoading } from "@hooks/useProductLoading";
import { paths } from "@paths";
import { FeaturedProducts } from "@utils/ssr";

import { MetaWrapper, NotFound } from "../../components/legacy";

interface CollectionData {
  details: CollectionDetails;
  attributes: Attribute[];
  featuredProducts: FeaturedProducts;
}
export type CollectionViewProps = {
  params: { slug: string } | undefined;
  data: ({ id: string } & CollectionData) | undefined | null;
};

export const CollectionView: NextPage<CollectionViewProps> = ({
  data: collection,
}) => {
  const attributes = useAttributesFiltering({ collectionId: collection?.id });

  const {
    filters,
    pricesFilter,
    locationFilter,
    handleClearFilters,
    handleFiltersChange,
    handlePricesFilter,
    handleLocationFilter,
    handleDeliveryFilter,
    deliveryFilter,
    handleOrderChange,
    hasDeliveryFilter,
    clientFiltered,
    keyword,
    setKeyword,
  } = useProductFiltering();

  const {
    products,
    loading,
    pageInfo,
    numberOfProducts,
    handleLoadMore,
    loadMoreError,
  } = useProductLoading({
    ssrData: null, // TODO: should have collection ssr data?
    ids: { collectionId: collection?.details?.id },
    filters,
    hasDeliveryFilter,
    clientFiltered,
    queryType:
      collection?.details.isWarehouseSaleCollection &&
      collection?.details.hasActiveSale
        ? "warehouse_sale"
        : "default",
  });

  const canonicalRef = collection && (
    <Head>
      <link
        rel="canonical"
        href={`${siteUrl}${paths.categoryBase}${collection.details.slug}`}
      />
    </Head>
  );

  return collection ? (
    <MetaWrapper
      meta={{
        description: collection.details.seoDescription || "",
        title: collection.details.seoTitle || "",
        type: "product.collection",
        image: collection.details?.backgroundImage?.url,
        url: ssrMode ? global.location.href : window.location.href,
      }}
    >
      {canonicalRef}
      <ProductListPage
        title={collection.details.name}
        aboveProductsDescription={
          collection.details.aboveProductsDescription
            ? collection.details.aboveProductsDescription
            : collection.details.seoDescription || ""
        }
        textContent={collection.details.description}
        attributes={attributes}
        numberOfProducts={numberOfProducts}
        clearFilters={handleClearFilters}
        displayLoader={!products.length && loading}
        hasNextPage={!!pageInfo?.hasNextPage}
        activeSortOption={filters.sortBy}
        filters={filters}
        products={products}
        onAttributeFiltersChange={handleFiltersChange}
        onLoadMore={handleLoadMore}
        loadMoreError={loadMoreError}
        activeFilters={Object.keys(filters?.attributes || {}).length}
        onOrder={handleOrderChange}
        pricesFilter={pricesFilter}
        setPricesFilter={handlePricesFilter}
        locationFilter={locationFilter}
        setLocationFilter={handleLocationFilter}
        deliveryFilter={deliveryFilter}
        setDeliveryFilter={handleDeliveryFilter}
        keyword={keyword}
        setKeyword={setKeyword}
        hasCollectionSale={collection.details.hasActiveSale}
        isWarehouseSaleCollection={collection.details.isWarehouseSaleCollection}
        warehouseSaleTitle={collection.details.warehouseSaleTitle}
        isFetching={loading}
      />
    </MetaWrapper>
  ) : (
    <NotFound />
  );
};
